import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import { Add, Edit, Delete, Download, CloudUpload } from '@mui/icons-material';
import api from '../api';
import Spinner from '../components/Spinner';
import normalizeImageUrl from '../utils/normalizeImageUrl';
import { useStaff } from '../contexts/StaffContext';

interface Material {
  _id: string;
  title: string;
  description: string;
  subject: { _id: string; name: string };
  className: string;
  fileType: string;
  fileName: string;
  fileUrl: string;
  isVisible: boolean;
  createdAt: string;
}

const StaffMaterials: React.FC = () => {
  const { staff } = useStaff();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [subjects, setSubjects] = useState<Array<{ _id: string; name: string }>>([]);
  const [classes, setClasses] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [uploadTab, setUploadTab] = useState<'url' | 'file'>(editingMaterial ? 'url' : 'url');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    subjectId: '',
    className: '',
    fileUrl: '',
    fileName: '',
    fileType: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Clear cache when staff member logs out or switches accounts
  const clearMaterialsCache = useCallback(() => {
    setMaterials([]);
    setEditingMaterial(null);
    setError(null);
    setSuccess(null);
    setOpenDialog(false);
  }, []);

  // Clear cache when staff context changes (logout or user switch)
  useEffect(() => {
    if (!staff) {
      clearMaterialsCache();
    }
  }, [staff, clearMaterialsCache]);

  const loadMaterials = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/staff/materials');
      setMaterials(response.data);
    } catch (err: any) {
      setError('Failed to load materials');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSubjects = useCallback(async () => {
    try {
      const response = await api.get('/staff/subjects');
      setSubjects(response.data);
    } catch (err: any) {
      setSubjects([]);
    }
  }, []);

  const loadClasses = useCallback(async () => {
    try {
      const response = await api.get('/staff/quizzes-data/classes-and-students');
      const classesData = Array.isArray(response.data?.classes) ? response.data.classes : [];
      setClasses(classesData);
    } catch (err: any) {
      setClasses([]);
    }
  }, []);

  useEffect(() => {
    loadMaterials();
    loadSubjects();
    loadClasses();
  }, [loadMaterials, loadSubjects, loadClasses]);

  const handleOpenDialog = (material?: Material) => {
    if (material) {
      setEditingMaterial(material);
      setUploadTab('url');
      setSelectedFile(null);
      setFormData({
        title: material.title,
        description: material.description,
        subjectId: material.subject._id,
        className: material.className,
        fileUrl: material.fileUrl,
        fileName: material.fileName,
        fileType: material.fileType
      });
    } else {
      setEditingMaterial(null);
      setUploadTab('url');
      setSelectedFile(null);
      setFormData({
        title: '',
        description: '',
        subjectId: '',
        className: staff?.className || '',
        fileUrl: '',
        fileName: '',
        fileType: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMaterial(null);
    setSelectedFile(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Auto-populate file type
      setFormData({
        ...formData,
        fileType: file.type.split('/')[1] || file.name.split('.').pop() || 'document',
        fileName: file.name
      });
      // Clear URL when file is selected
      setFormData((prev) => ({ ...prev, fileUrl: '' }));
    }
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (!formData.title || !formData.subjectId) {
        setError('Title and subject are required');
        return;
      }

      let payload: any = {
        title: formData.title,
        description: formData.description,
        subjectId: formData.subjectId,
        className: formData.className,
        fileType: formData.fileType,
        fileName: formData.fileName
      };

      // Handle file upload
      if (uploadTab === 'file' && selectedFile) {
        setFileUploading(true);
        const fileFormData = new FormData();
        fileFormData.append('file', selectedFile);

        try {
          const uploadResponse = await api.post('/uploads', fileFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          const uploadedData = uploadResponse.data?.data || uploadResponse.data;
          payload.fileUrl = uploadedData?.fileUrl || '';
          payload.fileName = uploadedData?.filename || payload.fileName;
        } catch (uploadErr: any) {
          setError(uploadErr.response?.data?.message || 'File upload failed');
          setFileUploading(false);
          return;
        }
        setFileUploading(false);
      } else if (uploadTab === 'url') {
        if (!formData.fileUrl) {
          setError('Please provide either a file or a URL');
          return;
        }
        payload.fileUrl = formData.fileUrl;
      } else {
        setError('Please select an upload method');
        return;
      }

      if (editingMaterial) {
        await api.put(`/staff/materials/${editingMaterial._id}`, payload);
        setSuccess('Material updated successfully');
      } else {
        await api.post('/staff/materials', payload);
        setSuccess('Material created successfully');
      }

      handleCloseDialog();
      loadMaterials();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save material');
    }
  };

  const handleDelete = async (materialId: string) => {
    if (window.confirm('Are you sure you want to delete this material?')) {
      try {
        await api.delete(`/staff/materials/${materialId}`);
        setSuccess('Material deleted successfully');
        loadMaterials();
      } catch (err: any) {
        setError('Failed to delete material');
      }
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Subject Materials</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Add Material
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Title</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Subject</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Class</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Visible</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {materials.length > 0 ? (
              materials.map((material) => (
                <TableRow key={material._id}>
                  <TableCell sx={{ color: 'text.primary' }}>{material.title}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>{material.subject?.name}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>{material.className || '—'}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>
                    <Chip label={material.fileType || 'document'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>{material.isVisible ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    {material.fileUrl && (
                      <IconButton
                        size="small"
                        href={normalizeImageUrl(material.fileUrl) || material.fileUrl}
                        target="_blank"
                        title="Download"
                      >
                        <Download fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => handleOpenDialog(material)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(material._id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                  No materials added yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMaterial ? 'Edit Material' : 'Add New Material'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Title"
            fullWidth
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Subject</InputLabel>
            <Select
              value={formData.subjectId}
              label="Subject"
              onChange={(e) => setFormData({ ...formData, subjectId: e.target.value })}
            >
              {subjects.map((subject) => (
                <MenuItem key={subject._id} value={subject._id}>
                  {subject.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Class</InputLabel>
            <Select
              value={formData.className}
              label="Class"
              onChange={(e) => setFormData({ ...formData, className: e.target.value })}
            >
              <MenuItem value="">All classes / school-wide</MenuItem>
              {classes.length > 0 ? (
                classes.map((className) => (
                  <MenuItem key={className} value={className}>
                    {className}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No classes available</MenuItem>
              )}
            </Select>
            {classes.length === 0 && (
              <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
                Note: No classes found. Ensure students are registered in the system.
              </Typography>
            )}
          </FormControl>

          {/* Upload Method Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3, mb: 2 }}>
            <Tabs value={uploadTab} onChange={(e, newValue) => setUploadTab(newValue as 'url' | 'file')}>
              <Tab label="URL" value="url" />
              <Tab label="Upload File" value="file" />
            </Tabs>
          </Box>

          {uploadTab === 'url' ? (
            <TextField
              label="File URL"
              fullWidth
              value={formData.fileUrl}
              onChange={(e) => setFormData({ ...formData, fileUrl: e.target.value })}
              margin="normal"
              helperText="Paste the URL of the file (must be publicly accessible)"
              placeholder="https://example.com/file.pdf"
            />
          ) : (
            <Box sx={{ py: 2 }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                fullWidth
                disabled={fileUploading}
              >
                {selectedFile ? `Selected: ${selectedFile.name}` : 'Choose File'}
                <input type="file" hidden onChange={handleFileSelect} />
              </Button>
              {fileUploading && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <CircularProgress size={24} />
                  <Typography variant="caption" sx={{ ml: 1 }}>
                    Uploading...
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <TextField
            label="File Type (auto-detected)"
            fullWidth
            value={formData.fileType}
            onChange={(e) => setFormData({ ...formData, fileType: e.target.value })}
            margin="normal"
            placeholder="pdf, doc, image, video, etc."
            disabled
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={fileUploading}>
            {editingMaterial ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffMaterials;
